// Tab positions for imported notes on fretted tracks.

import { midiOf, fingerChord, averageFret, type FretPos } from '@simplenotation/theory';
import type { Track, Beat } from '../model/types';

function octaveShiftSemis(b: Beat): number {
  return b.octaveShift ? (Math.abs(b.octaveShift) === 15 ? 24 : 12) * Math.sign(b.octaveShift) : 0;
}

/**
 * Give notes without a tab position a string/fret (fretted tracks only),
 * completing missing frets from given strings, keeping tied notes on their
 * string and preferring positions near the previous ones.
 */
export function fingerTrack(track: Track): void {
  const tuning = track.tuning ?? [];
  if (!tuning.length) return;
  const capo = track.capo ?? 0;
  const voices = Math.max(1, ...track.bars.map((tb) => tb.voices.length));
  for (let v = 0; v < voices; v++) {
    const recent: FretPos[] = [];
    let ties = new Map<number, FretPos>();
    for (const tb of track.bars) {
      for (const b of tb.voices[v] ?? []) {
        if (b.rest || !b.notes.length) {
          if (!b.grace) ties = new Map();
          continue;
        }
        const sh = octaveShiftSemis(b);
        const sounding = (n: Beat['notes'][number]) => midiOf(n.pitch) + track.writtenToSounding + sh;
        for (const n of b.notes) {
          if (n.string !== undefined && (n.string < 1 || n.string > tuning.length)) {
            delete n.string;
            delete n.fret;
          }
          if (n.string !== undefined && n.fret === undefined) {
            const f = sounding(n) - tuning[n.string - 1] - capo;
            if (f >= 0 && f <= 30) n.fret = f;
            else delete n.string;
          }
          if (n.string === undefined) delete n.fret;
        }
        const used = new Set(b.notes.filter((n) => n.string !== undefined).map((n) => n.string!));
        // Tied continuations keep their string.
        if (!b.grace) {
          for (const n of b.notes) {
            if (n.string !== undefined) continue;
            const t = ties.get(sounding(n));
            if (t && !used.has(t.string)) {
              n.string = t.string;
              n.fret = t.fret;
              used.add(t.string);
            }
          }
        }
        const missing = b.notes.filter((n) => n.string === undefined);
        if (missing.length) {
          const res = fingerChord(
            missing.map((n) => sounding(n)),
            { tuning, capo, handPos: averageFret(recent.slice(-6)) },
          );
          if (res) {
            missing.forEach((n, i) => {
              const p = res[i];
              if (p && !used.has(p.string)) {
                n.string = p.string;
                n.fret = p.fret;
                used.add(p.string);
              }
            });
          } else {
            // Unplayable as a chord: place notes one by one where possible.
            for (const n of missing) {
              const r = fingerChord([sounding(n)], { tuning, capo, handPos: averageFret(recent.slice(-6)) });
              const p = r?.[0];
              if (p && !used.has(p.string)) {
                n.string = p.string;
                n.fret = p.fret;
                used.add(p.string);
              }
            }
          }
        }
        for (const n of b.notes) if (n.string !== undefined && n.fret !== undefined && n.fret > 0) recent.push({ string: n.string, fret: n.fret });
        if (recent.length > 12) recent.splice(0, recent.length - 12);
        if (!b.grace) {
          ties = new Map();
          for (const n of b.notes) if (n.tieToNext && n.string !== undefined && n.fret !== undefined) ties.set(sounding(n), { string: n.string, fret: n.fret });
        }
      }
    }
  }
}
